import { useMemo } from 'react';

import type { NavigationItemId } from '@config/navigation/navigationItems';
import type { SettingsSection } from '@config/paths/settingsSections';
import type { ProductModuleId } from '@config/products/productModules';

import {
	getLockedNavItems,
	getSettingsSectionGate,
	isModuleLocked as isModuleLockedPure,
} from '@config/products/productGating';

import { useCurrentServicesProvider } from './useCurrentServicesProvider';

interface ProductGatingResult {
	/** Whether a specific product module is locked */
	isModuleLocked: (moduleId: ProductModuleId) => boolean;
	/** Whether a settings section is gated and locked; returns the module ID if so */
	getSettingsSectionGate: (section: SettingsSection) => {
		locked: boolean;
		moduleId?: ProductModuleId;
	};
	/** Set of nav item IDs that are currently locked */
	lockedNavItems: Set<NavigationItemId>;
	/** Whether the provider data is still loading */
	isLoading: boolean;
}

/**
 * Hook for checking product subscription gating across nav items and settings sections.
 * Fetches the current provider once and returns bound utility functions.
 *
 * Usage:
 *   const { isModuleLocked, getSettingsSectionGate, lockedNavItems } = useProductGating();
 */
export function useProductGating(): ProductGatingResult {
	const { data: provider, isLoading } = useCurrentServicesProvider();

	const lockedNavItems = useMemo(() => getLockedNavItems(provider), [provider]);

	return {
		isModuleLocked: (moduleId) => isModuleLockedPure(provider, moduleId),
		getSettingsSectionGate: (section) => getSettingsSectionGate(provider, section),
		lockedNavItems,
		isLoading,
	};
}
